import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	EditWalletMutationParams,
	Wallet,
	WalletDetailedWithAccountId,
	createEditWalletMutation,
} from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type EditWalletFormValue = {
	numberOfBets: number;
	turnover: number;
	cashdeskProfit: number;
};

export type EditWalletModel = ModalFormModel<WalletDetailedWithAccountId, EditWalletFormValue> & {
	editWalletMutation: Mutation<EditWalletMutationParams, Wallet, JsonApiRequestError>;
	$currency: Store<string | null>;
	$pending: Store<boolean>;
};

export const createEditWalletModel = (): EditWalletModel => {
	const editWalletMutation = createEditWalletMutation();

	const model = createModalFormModel<WalletDetailedWithAccountId, EditWalletFormValue>({
		fields: {
			numberOfBets: {
				init: 0,
				rules: [notEmptyRule, greaterZeroRule],
			},
			turnover: {
				init: 0,
				rules: [notEmptyRule, greaterZeroRule],
			},
			cashdeskProfit: {
				init: 0,
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	const $currency = model.$modalData.map((wallet) => wallet?.currency ?? null);

	const { $pending } = editWalletMutation;

	sample({
		clock: model.modalButtonClicked,
		fn: (wallet) => ({
			numberOfBets: wallet.numberOfBets,
			turnover: wallet.turnover,
			cashdeskProfit: wallet.cashdeskProfit,
		}),
		target: [model.form.setValues, model.form.setInitialValues],
	});

	sample({
		clock: model.form.submitted,
		source: {
			formWallet: model.form.$values,
			wallet: model.$modalData,
		},
		fn: ({ formWallet, wallet }) => ({
			id: wallet!.id,
			numberOfBets: formWallet.numberOfBets,
			turnover: formWallet.turnover,
			cashdeskProfit: formWallet.cashdeskProfit,
		}),
		target: editWalletMutation.start,
	});

	chain(editWalletMutation.finished.success, model.switcher.disable);

	notify(editWalletMutation.finished.success, 'edit-wallet.success');

	chain(model.modalClosed, editWalletMutation.reset);

	return {
		editWalletMutation,
		$currency,
		$pending,
		...model,
	};
};
